import { morningHeaderPresets } from '@/components/morning-header-presets'
import { cn } from '@/lib/utils'

const morningHeaderPreset = morningHeaderPresets.glass

/** Accent tone keyed to the section surface the eyebrow sits on. */
export type MorningEyebrowDotTone = 'ember' | 'cream' | 'orange'

const eyebrowDotToneClassName: Record<
  MorningEyebrowDotTone,
  { ring: string; core: string; glow: string }
> = {
  ember: {
    ring: 'bg-ember/15',
    core: 'bg-ember',
    glow: 'shadow-[0_0_10px_rgba(194,84,10,0.18)]',
  },
  cream: {
    ring: 'bg-cream/20',
    core: 'bg-cream',
    glow: 'shadow-[0_0_10px_rgba(248,242,231,0.22)]',
  },
  orange: {
    // Same sun-core structure as ember/cream; `orange-500` matches logo / CTA.
    ring: 'bg-orange-500/15',
    core: 'bg-orange-500',
    glow: 'shadow-[0_0_10px_rgba(255,105,0,0.18)]',
  },
}

export function MorningEyebrowDot({
  className,
  animate = true,
  tone = 'ember',
}: {
  className?: string
  animate?: boolean
  /** `ember` on light surfaces; `cream` on ember / ocean; `orange` for brand accent. */
  tone?: MorningEyebrowDotTone
}) {
  const colors = eyebrowDotToneClassName[tone]

  return (
    <span
      aria-hidden
      data-tone={tone}
      className={cn(
        'relative flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full',
        colors.ring,
        colors.glow,
        animate && 'eyebrow-dot-throb',
        className,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', colors.core)} />
    </span>
  )
}

export function MorningEyebrowPill({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={cn(
        morningHeaderPreset.barClassName,
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium shadow-none sm:gap-1.5 sm:px-4 sm:py-1',
      )}
    >
      <MorningEyebrowDot tone="orange" />
      <span className={cn(morningHeaderPreset.navClassName, 'text-white/85')}>
        {children}
      </span>
    </div>
  )
}
